import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { Strings } from '../constants/Strings';
import { savePerk, unsavePerk, redeemPerk, getPerk } from '@cobu/api-client';
import { useSelector } from 'react-redux';
import type { PerkData, ActionSheetOption, UsePerkDetailProps } from '@cobu/types';
import { showErrorMessage, showInfoMessage } from '../utils/messageToast';
import type { RootState } from '../store';
import { logger } from '../utils/logger';

interface UsePerkDetailReturn {
  // Data
  perk: PerkData | undefined;
  redemptionsCount: number;

  // Loading states
  isLoading: boolean;

  // Error states
  error: string | null;

  // Perk interaction states
  isRedeemed: boolean;
  isSaved: boolean;
  savedPerkId: string | null;

  // Mutations
  savePerkMutation: UseMutationResult<any, unknown, void, unknown>;
  unsavePerkMutation: UseMutationResult<any, unknown, void, unknown>;
  redeemPerkMutation: UseMutationResult<any, unknown, void, unknown>;

  // Handlers
  handleRedeemPress: () => Promise<void>;
  handleSavePress: () => Promise<void>;
  handleEmailPress: () => void;
  handlePhonePress: () => void;
  handleClose: () => void;
  handleRetry: () => void;

}

export const usePerkDetail = ({ perkId }: UsePerkDetailProps): UsePerkDetailReturn => {
  const strings = Strings.get();
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const normalizeUrl = useCallback((link: string): string => {
    const trimmed = (link || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (/^\/\//.test(trimmed)) return `https:${trimmed}`;
    if (trimmed.startsWith('/')) return new URL(trimmed, window.location.origin).toString();
    return `https://${trimmed}`;
  }, []);

  // Fetch perk data
  const {
    data: perk,
    isLoading: isLoadingPerk,
    error: errorPerk,
    refetch: refetchPerk,
  } = useQuery({
    queryKey: ['perk', perkId],
    queryFn: () => getPerk(perkId),
    enabled: !!perkId,
  });

  // Local state for perk interactions
  const [isRedeemed, setIsRedeemed] = useState(perk?.redeemedByUser || false);
  const [isSaved, setIsSaved] = useState(perk?.savedPerkId !== null || false);
  const [savedPerkId, setSavedPerkId] = useState<string | null>(perk?.savedPerkId || null);

  // Update local state when perk data changes
  useEffect(() => {
    if (perk) {
      setIsRedeemed(perk.redeemedByUser || false);
      setIsSaved(perk?.savedPerkId !== null || false);
      setSavedPerkId(perk?.savedPerkId || null);
    }
  }, [perk]);

  // Calculate redemptions count
  const redemptionsCount = perk?.redeemCount || 0;

  // Mutations
  const savePerkMutation = useMutation({
    mutationFn: async () => {
      if (!user?.uuid || !perk) throw new Error('User/perk missing');
      return await savePerk({ perk: perk.uuid, user: user.uuid });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['perk', perkId] });
    },
  });
  const unsavePerkMutation = useMutation({
    mutationFn: async () => {
      if (!perk?.savedPerkId) throw new Error('savedPerkId missing');
      return await unsavePerk({ savedPerkId: perk.savedPerkId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['perk', perkId] });
    },
  });
  const redeemPerkMutation = useMutation({
    mutationFn: async () => {
      if (!user?.uuid || !user.building?.uuid || !perk) throw new Error('User/perk missing');
      return await redeemPerk({ perk: perk.uuid, user: user.uuid, building: user.building.uuid });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['perk', perkId] });
    },
  });

  const handleClose = useCallback(() => {
    navigate('/perks');
  }, [navigate]);

  const handleRetry = useCallback(() => {
    refetchPerk();
  }, [refetchPerk]);

  const handleRedeemPress = useCallback(async () => {
    if (!user?.uuid || !user.building?.uuid || !perk) {
      showErrorMessage(`${strings.common.error.title}: ${strings.hooks.perkDetail.errors.userNotAvailable}`);
      return;
    }

    try {
      setIsRedeemed(true);

      await redeemPerkMutation.mutateAsync();
    //   analyticsService.trackEvent(AnalyticsEvent.REDEEM_PERK_CLICK, {
    //     perkUuid: perk.uuid,
    //     userUuid: user.uuid,
    //   });

      // Handle different link types
      if (perk.redeemLink) {
        const targetUrl = normalizeUrl(perk.redeemLink);

        // Open inside embedded browser route
        navigate('/webview', {
          state: {
            url: targetUrl,
            title: perk.name,
          },
        });
      }
    } catch (error) {
      logger.error('Error redeeming perk:', error);
      setIsRedeemed(false);
      showErrorMessage('Error: Failed to redeem perk. Please try again.');
    }
  }, [user, perk, redeemPerkMutation, normalizeUrl, navigate]);

  const handleSavePress = useCallback(async () => {
    if (!user?.uuid || !perk) {
      showErrorMessage('Error: User information not available');
      return;
    }

    try {
      if (isSaved && savedPerkId) {
        await unsavePerkMutation.mutateAsync();
        setIsSaved(false);
        setSavedPerkId(null);
      } else {
        const response = await savePerkMutation.mutateAsync();
        setIsSaved(true);
        setSavedPerkId((response as any)?.savedPerkId || null);
      }
    } catch (error) {
      logger.error('Error saving/unsaving perk:', error);
      showErrorMessage('Error: Failed to save perk. Please try again.');
    }
  }, [user, perk, isSaved, savedPerkId, savePerkMutation, unsavePerkMutation]);

  const handleEmailPress = useCallback(() => {
    if (perk?.contactEmail) {
      const emailUrl = `mailto:${perk.contactEmail}`;
      window.location.href = emailUrl;
    }
  }, [perk?.contactEmail]);

  const handlePhonePress = useCallback(() => {
    if (!perk) return;

    const options: ActionSheetOption[] = [];

    if (perk.contactPhone) {
      options.push({
        text: `Call: ${perk.contactPhone}`,
        onPress: () => window.location.href = `tel:${perk.contactPhone}`,
      });
    }

    if (perk.contactSMSNumber) {
      options.push({
        text: `Text: ${perk.contactSMSNumber}`,
        onPress: () => window.location.href = `sms:${perk.contactSMSNumber}`,
      });
    }

    options.push({
      text: 'Cancel',
      style: 'cancel',
      onPress: () => {},
    });

    if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
      showInfoMessage(options.map(opt => opt.text).join('\n'));
    } else {
      // Android fallback - show alert
      showInfoMessage('Contact Options: Choose how to contact\n' + options.map(opt => opt.text).join('\n'));
    }
  }, [perk?.contactPhone, perk?.contactSMSNumber]);

  return {
    // Data
    perk,
    redemptionsCount,

    // Loading states
    isLoading: isLoadingPerk,

    // Error states
    error: errorPerk?.message || null,

    // Perk interaction states
    isRedeemed,
    isSaved,
    savedPerkId,

    // Mutations
    savePerkMutation,
    unsavePerkMutation,
    redeemPerkMutation,

    // Handlers
    handleRedeemPress,
    handleSavePress,
    handleEmailPress,
    handlePhonePress,
    handleClose,
    handleRetry,

  };
};
